import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Copy, Check, Loader2, X, Trash2, Clock } from 'lucide-react';
import api from '@/lib/api';

function formatExpiresIn(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

const DURATION_OPTIONS = [
  { labelKey: 'sharing.hours1', ms: 3600000 },
  { labelKey: 'sharing.hours24', ms: 86400000 },
  { labelKey: 'sharing.days7', ms: 604800000 },
  { labelKey: 'sharing.days30', ms: 2592000000 },
];

export default function ShareDialog({ deviceId, deviceName, open, onClose }) {
  const { t } = useTranslation();
  const [durationMs, setDurationMs] = useState(DURATION_OPTIONS[1].ms);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const loadShares = useCallback(async () => {
    if (!deviceId) return;
    setSharesLoading(true);
    try {
      const users = await api.getDeviceShares(deviceId);
      const now = Date.now();
      const active = (Array.isArray(users) ? users : []).filter(
        (u) => u?.temporary && (!u.expirationTime || new Date(u.expirationTime).getTime() > now)
      );
      setShares(active);
    } catch {
      setShares([]);
    } finally {
      setSharesLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    if (open) loadShares();
  }, [open, loadShares]);

  const handleRevoke = async (userId) => {
    setRevokingId(userId);
    try {
      await api.deleteShare(userId);
      setShares((prev) => prev.filter((s) => s.id !== userId));
    } catch (err) {
      setError(err.message || 'Failed to revoke share');
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopyExisting = async (token) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/share/${token}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // noop
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setShareUrl('');
    try {
      const expiration = new Date(Date.now() + durationMs).toISOString();
      const response = await api.shareDevice(deviceId, expiration);
      // Response may be a token object or plain string
      const token = response?.token || response;
      setShareUrl(`${window.location.origin}/share/${token}`);
      loadShares();
    } catch (err) {
      setError(err.message || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input text
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setError('');
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-6 w-full max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 end-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
        >
          <X size={16} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <Link2 size={20} className="text-blue-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">{t('sharing.shareLink')}</h3>
            <p className="text-xs text-slate-500">{deviceName}</p>
          </div>
        </div>

        {/* Expiration selector */}
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          {t('sharing.expiration')}
        </label>
        <select
          value={durationMs}
          onChange={(e) => setDurationMs(Number(e.target.value))}
          className="w-full text-sm rounded-xl px-3 py-2.5 mb-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 transition-all"
        >
          {DURATION_OPTIONS.map((opt) => (
            <option key={opt.ms} value={opt.ms}>{t(opt.labelKey)}</option>
          ))}
        </select>

        {/* Generate button */}
        {!shareUrl && (
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', boxShadow: '0 2px 8px rgba(59,130,246,0.3)' }}
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Link2 size={16} />
            )}
            {t('sharing.generateLink')}
          </button>
        )}

        {/* Error */}
        {error && (
          <p className="text-xs text-red-500 mt-2 text-center">{error}</p>
        )}

        {/* Generated URL */}
        {shareUrl && (
          <div className="mt-1">
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              {t('sharing.sharing')}
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 text-xs rounded-xl px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 outline-none select-all"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                  copied
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100'
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? t('sharing.copied') : t('sharing.copyLink')}
              </button>
            </div>
          </div>
        )}

        {/* Active shares list */}
        <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-slate-600">
              {t('sharing.activeShares', 'Active shares')}
            </label>
            {sharesLoading && <Loader2 size={12} className="animate-spin text-slate-400" />}
          </div>
          {!sharesLoading && shares.length === 0 && (
            <p className="text-xs text-slate-400 italic">
              {t('sharing.noActiveShares', 'No active shares for this device')}
            </p>
          )}
          {shares.length > 0 && (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {shares.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                >
                  <Clock size={12} className="text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-600 dark:text-slate-300 truncate">
                      /share/{s.token?.slice(0, 12)}…
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {t('sharing.expiresIn', 'Expires in')} {formatExpiresIn(s.expirationTime)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyExisting(s.token)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                    title={t('sharing.copyLink')}
                  >
                    <Copy size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRevoke(s.id)}
                    disabled={revokingId === s.id}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                    title={t('sharing.revoke', 'Revoke')}
                  >
                    {revokingId === s.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
